import { Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  Subject,
  Observable,
  of,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  catchError,
  map,
  tap,
} from 'rxjs';
import { Folder, FolderPlanogram } from '../../models/folder';
import { Merchandise } from '../../models/merchandise';
import { RangingDataSearchEntry } from '../../models/ranging-data-search-entry';
import { StoreSearchEntry } from '../../models/store-search-entry';
import { ApiService } from '../../services/api.service';
import { FolderApiService } from '../../services/folder-api.service';
import { PlanogramApiService } from '../../services/planogram-api.service';
import { ToastService } from '../../services/toasts.service';
import { CreatePlanogramRequest } from '../../requests';
import { PlanogramService } from '../../services/planogram.service';
import { RangeType } from '../../models/range-type';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { NgIf, NgFor, AsyncPipe, DatePipe } from '@angular/common';

@Component({
    selector: 'app-new-planogram-page',
    templateUrl: './new-planogram.component.html',
    styleUrl: './new-planogram.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        NgbTypeahead,
        NgIf,
        NgFor,
        AsyncPipe,
        DatePipe,
    ],
})
export class NewPlanogramComponent implements OnInit {
  planogramForm: FormGroup;
  storeSearchTerm = new Subject<string>();
  merchSearchTerm = new Subject<string>();

  storeResults$: Observable<StoreSearchEntry[]> = of([]);
  merchResults$: Observable<Merchandise[]> = of([]);
  dataLoads$: Observable<RangingDataSearchEntry[] | null> = of(null);
  availableFolders$: Observable<{ id: string; name: string }[]> = of([]);

  RangeType = RangeType;
  isLoadingDataLoads = false;
  isLoadingFolders = false;
  isSubmitting = false;
  isPsaImport = false;

  psaFile: File | null = null;
  public initialFolderIdFromRoute: string | null = null;

  private readonly apiUrl = environment.apiUrl;

  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private apiService: ApiService,
    private planogramApiService: PlanogramApiService,
    private folderApiService: FolderApiService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.planogramForm = this.fb.group({
      mode: ['dataload', Validators.required],
      store: [null, Validators.required],
      merchandise: [null, Validators.required],
      dataLoadGuid: [''],
      folderId: [''],
      psaFile: [null],
      salesStart: [''],
      salesEnd: [''],
      usePsaProductData: [false],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.initialFolderIdFromRoute = params['folderId'] || null;
      if (this.initialFolderIdFromRoute) {
        this.planogramForm.patchValue({
          folderId: this.initialFolderIdFromRoute,
        });
      }
    });
    
    // --- Set default date values ---
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 3, 1); // 1st day 3 months ago
    const end = new Date(now.getFullYear(), now.getMonth(), 1); // 1st day of current month
    this.planogramForm.patchValue({
      salesStart: start.toISOString().split('T')[0],
      salesEnd: end.toISOString().split('T')[0],
    });

    this.setupStoreSearch();
    this.setupMerchSearch();
    this.loadFolders();

    this.planogramForm.get('store')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.isPsaImport) this.loadDataLoads();
    });
    this.planogramForm.get('merchandise')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.isPsaImport) this.loadDataLoads();
    });
    this.planogramForm.get('mode')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mode) => {
      this.isPsaImport = mode === 'psa';
      if (this.isPsaImport) {
        this.planogramForm.get('dataLoadGuid')?.clearValidators();
        this.planogramForm.get('psaFile')?.setValidators([Validators.required]);
        this.planogramForm.get('salesStart')?.setValidators([Validators.required]);
        this.planogramForm.get('salesEnd')?.setValidators([Validators.required]);
      } else {
        this.planogramForm.get('dataLoadGuid')?.setValidators([Validators.required]);
        this.planogramForm.get('psaFile')?.clearValidators();
        this.planogramForm.get('salesStart')?.clearValidators();
        this.planogramForm.get('salesEnd')?.clearValidators();
      }
      this.planogramForm.get('dataLoadGuid')?.updateValueAndValidity();
      this.planogramForm.get('psaFile')?.updateValueAndValidity();
      this.planogramForm.get('salesStart')?.updateValueAndValidity();
      this.planogramForm.get('salesEnd')?.updateValueAndValidity();
    });
  }

  private setupStoreSearch(): void {
    this.storeResults$ = this.storeSearchTerm.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((term) => term.length >= 2),
      switchMap((term) =>
        this.apiService.searchStores(term).pipe(catchError(() => of([])))
      )
    );
  }

  storeInputFormatter = (store: StoreSearchEntry) => store.name;
  storeResultFormatter = (store: StoreSearchEntry) =>
    `${store.name} (ID: ${store.storeId})`;

  onStoreSelected(event: { item: StoreSearchEntry }): void {
    this.planogramForm.patchValue({ store: event.item });
  }

  private setupMerchSearch(): void {
    this.merchResults$ = this.merchSearchTerm.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => {
        const store = this.planogramForm.get('store')?.value as StoreSearchEntry | null;
        if(!store){
          return of([]);
        }
        return this.apiService.getMerchandiseCategories(term, store!.storeGuid).pipe(catchError(() => of([])));
      })
    );
  }

  merchInputFormatter = (merch: Merchandise) => merch.name;
  merchResultFormatter = (merch: Merchandise) => merch.name;

  onMerchSelected(event: { item: Merchandise }): void {
    this.planogramForm.patchValue({ merchandise: event.item });
  }

  public searchStores = (text$: Observable<string>): Observable<StoreSearchEntry[]> =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => this.apiService.searchStores(term).pipe(catchError(() => of([]))))
    );

  public searchMerch = (text$: Observable<string>): Observable<Merchandise[]> =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => {
        const store = this.planogramForm.get('store')?.value as StoreSearchEntry | null;
        if(!store){
          return of([]);
        }
        return this.apiService.getMerchandiseCategories(term, store!.storeGuid).pipe(catchError(() => of([])));
      })
    );

  loadDataLoads(): void {
    if (this.isPsaImport) return;
    const store = this.planogramForm.get('store')?.value as StoreSearchEntry | null;
    const merch = this.planogramForm.get('merchandise')?.value as Merchandise | null;
    if (!store || !merch) return;

    this.isLoadingDataLoads = true;
    this.dataLoads$ = this.planogramApiService
      .getPlanogramAvailableData(store.storeGuid, merch.id)
      .pipe(
        map((data) => (data ? data : [])),
        tap(() => (this.isLoadingDataLoads = false)),
        catchError((err) => {
          this.isLoadingDataLoads = false;
          this.toastService.show('Failed to load data loads.', {
            classname: 'bg-danger text-light',
            delay: 5000,
          });
          return of(null);
        })
      );
  }

  loadFolders(): void {
    this.isLoadingFolders = true;
    this.availableFolders$ = this.folderApiService
      .getUserRootFolders(false)
      .pipe(
        map((folders) => this.flattenFolders(folders)),
        tap(() => (this.isLoadingFolders = false)),
        catchError(() => {
          this.isLoadingFolders = false;
          this.toastService.show('Failed to load folders.', {
            classname: 'bg-danger text-light',
            delay: 5000,
          });
          return of([]);
        })
      );
  }

  private flattenFolders(
    folders: Folder[],
    prefix = '',
    level = 0
  ): { id: string; name: string; level: number }[] {
    let flatList: { id: string; name: string; level: number }[] = [];
    for (const folder of folders) {
      flatList.push({
        id: folder.guid,
        name:
          (level > 0 ? '\u00A0\u00A0'.repeat(level) + '↳ ' : '') + folder.name,
        level,
      });
      if (folder.subFolders?.length) {
        flatList = flatList.concat(
          this.flattenFolders(folder.subFolders, prefix + folder.name + ' / ', level + 1)
        );
      }
    }
    return flatList;
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.psa')) {
      this.psaFile = file;
      this.planogramForm.patchValue({ psaFile: file });
    } else {
      this.toastService.show('Please select a valid .psa file.', {
        classname: 'bg-warning text-dark',
        delay: 4000,
      });
    }
  }

  async onSubmit() {
    if (this.planogramForm.invalid) {
      this.toastService.show('Please fill in all required fields.', {
        classname: 'bg-warning text-dark',
        delay: 3000,
      });
      this.planogramForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const formValue = this.planogramForm.value;
    const store = formValue.store as StoreSearchEntry;
    const merch = formValue.merchandise as Merchandise;

    if (this.isPsaImport && this.psaFile) {
      // PSA file upload
      const formData = new FormData();
      formData.append('storeId', store.storeGuid);
      formData.append('merchId', merch.id.toString());
      if (formValue.folderId) formData.append('folderId', formValue.folderId);
      formData.append('salesStart', formValue.salesStart);
      formData.append('salesEnd', formValue.salesEnd);
      if (formValue.usePsaProductData) formData.append('usePsaProductData', formValue.usePsaProductData);
      formData.append('file', this.psaFile);

      try {
        const planogram = await this.http
          .post<FolderPlanogram>(`${this.apiUrl}/import/import-psa`, formData)
          .toPromise();

        if (planogram) {
          let stateId = null;
          if(planogram.states && planogram.states.length > 0) {
             stateId = planogram.states[0].guid;
          }
          this.toastService.show('PSA file imported successfully.', {
            classname: 'bg-success text-light',
            delay: 3000,
          });
          this.router.navigate(['/planogram', planogram.guid, stateId].filter(i => !!i));
        }
      } catch (err) {
        console.error('Failed to import PSA file', err);
        this.toastService.show('Failed to import PSA file.', {
          classname: 'bg-danger text-light',
          delay: 4000,
        });
      } finally {
        this.isSubmitting = false;
      }
      return;
    }

    // Normal dataload flow
    const request = new CreatePlanogramRequest(
      store.storeGuid,
      merch.id,
      formValue.dataLoadGuid,
      formValue.folderId || null
    );

    try {
      const planogram = await this.planogramApiService.createPlanogram(request);
      this.toastService.show('Planogram created successfully!', {
        classname: 'bg-success text-light',
        delay: 3000,
      });
      this.router.navigate(['/planogram', planogram.guid]);
    } 
    catch (err) {
      console.error(err);
      this.toastService.show('Failed to create planogram.', {
        classname: 'bg-danger text-light',
        delay: 4000,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  onCancel(): void {
    history.back();
  }
}